import type { Run } from "../simulation/types";
import { validateRun } from "../simulation/engine";
export interface StoredSession {
  version: 1;
  run: Run;
  comparison: Run | null;
  savedAt: string;
}
const KEY = "pulse-session-v1";
async function db(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("pulse-city", 1);
    request.onupgradeneeded = () =>
      request.result.createObjectStore("sessions");
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error("Storage blocked"));
  });
}
export async function saveSession(
  session: StoredSession,
): Promise<"saved" | "memory"> {
  try {
    const database = await db();
    await new Promise<void>((resolve, reject) => {
      const tx = database.transaction("sessions", "readwrite");
      tx.objectStore("sessions").put(session, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    database.close();
    return "saved";
  } catch {
    try {
      localStorage.setItem(KEY, JSON.stringify(session));
      return "saved";
    } catch {
      return "memory";
    }
  }
}
export async function loadSession(): Promise<StoredSession | null> {
  let v: unknown;
  try {
    const database = await db();
    v = await new Promise((resolve, reject) => {
      const r = database
        .transaction("sessions")
        .objectStore("sessions")
        .get(KEY);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
    database.close();
  } catch {
    /* fall through to the storage fallback */
  }
  if (!v) {
    try {
      v = JSON.parse(localStorage.getItem(KEY) || "null");
    } catch {
      return null;
    }
  }
  if (!v || typeof v !== "object") return null;
  const s = v as StoredSession;
  const run = validateRun(s.run);
  if (s.version !== 1 || !run) return null;
  return {
    version: 1,
    run,
    comparison: validateRun(s.comparison),
    savedAt: s.savedAt,
  };
}
export function downloadRun(run: Run, comparison: Run | null) {
  const data = {
    format: "pulse-run",
    version: 1,
    run,
    comparison,
    exportedAt: new Date().toISOString(),
    notice:
      "Experimental synthetic simulation. Not real-world operational data.",
  };
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = "PULSE-scenario.json";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
